import { Layers, Sparkles, X } from 'lucide-react';
import { Button, Badge } from '@aaas/shared';
import type { Widget } from '../../types/widget.types';

interface AssociatedWidgetsPanelProps {
  associatedWidgets: Widget[];
  allWidgets: Widget[];
  currentIndex: number;
  onWidgetClick: (index: number) => void;
  onRemove: (widget: Widget) => void;
  isRemoving?: boolean;
}

export const AssociatedWidgetsPanel = ({
  associatedWidgets,
  allWidgets,
  currentIndex,
  onWidgetClick,
  onRemove,
  isRemoving = false,
}: AssociatedWidgetsPanelProps) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 h-full flex flex-col">
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center">
            <Layers className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Associated Widgets</h3>
            <p className="text-sm text-gray-600">
              {associatedWidgets.length} widget{associatedWidgets.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="overflow-y-auto flex-1" style={{ maxHeight: 'calc(700px - 96px - 40px)' }}>
        <div className="p-6">
          {associatedWidgets.length === 0 ? (
            // Empty State
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 text-sm mb-2">No widgets associated yet</p>
              <p className="text-xs text-gray-400">
                Use the Add button to associate widgets with this space
              </p>
            </div>
          ) : (
            // Widget List
            <div className="space-y-3">
              {associatedWidgets.map((widget) => {
                const widgetIndex = allWidgets.findIndex((w) => w.id === widget.id);
                const isCurrent = widgetIndex === currentIndex;

                return (
                  <div
                    key={widget.id}
                    onClick={() => onWidgetClick(widgetIndex)}
                    className={`
                      group cursor-pointer rounded-xl p-4 border-2 transition-all duration-200
                      ${
                        isCurrent
                          ? 'border-blue-600 bg-blue-50 shadow-lg ring-2 ring-blue-200'
                          : 'border-gray-200 bg-gradient-to-br from-gray-50 to-white hover:border-blue-300 hover:shadow-md'
                      }
                    `}
                  >
                    {/* Widget Info */}
                    <div className="flex items-start gap-3">
                      {/* Widget icon */}
                      <div
                        className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 shadow-sm"
                        title={widget.name}
                      >
                        <span className="text-xl">{widget.avatar_icon || '🤖'}</span>
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm text-gray-900 mb-1 truncate">
                          {widget.name}
                        </h4>
                        <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                          {widget.description || 'No description'}
                        </p>

                        {/* Metadata */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            {widget.position}
                          </Badge>
                          <Badge
                            variant={widget.is_active ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {widget.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          {(widget.spaces?.length ?? 0) > 1 && (
                            <Badge variant="outline" className="text-xs text-gray-500">
                              {widget.spaces?.length ?? 0} spaces
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Delete Button */}
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemove(widget);
                        }}
                        variant="ghost"
                        disabled={isRemoving}
                        className="w-8 h-8 p-1 flex items-center justify-center hover:bg-red-100 transition-colors disabled:opacity-50 flex-shrink-0"
                        aria-label="Remove widget"
                      >
                        <X className="w-5 h-5 text-red-500" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="w-full px-4 py-3 bg-gray-50 border-t border-gray-200 flex-shrink-0">
        <p className="text-xs text-gray-500 text-center">Click to preview • Hover to remove</p>
      </div>
    </div>
  );
};
